import React, { useEffect, useState, useMemo } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../styles/ProductPage.css";
import marbleDB from "../Data/marble_database.json";

const ProductPage = () => {
  const { slug } = useParams();
  const location = useLocation();

  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  useEffect(() => {
    const t = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(t);
  }, [slug]);

  const product = useMemo(
    () => marbleDB.find((p) => p.slug === slug),
    [slug]
  );

  const related = useMemo(() => {
    if (!product) return [];
    return marbleDB.filter(
      (p) => p.primary === product.primary && p.slug !== slug
    ).slice(0, 8);
  }, [product, slug]);

  if (loading) {
    return (
      <div className="container">
        <div className="hero-section skeleton">
          <div className="image-skeleton" />
          <div className="text-skeleton" />
        </div>
      </div>
    );
  }

  if (!product) {
    return <h1 style={{ padding: "40px" }}>Product not found</h1>;
  }

  const images = product.images || [];

  const changeSlide = (dir) => {
    setCurrentSlide((prev) => (prev + dir + images.length) % images.length);
  };

  const seoKeywordType =
    slug.charCodeAt(0) % 2 === 0 ? "Best" : "Top";

  const title = `${product.name} – ${seoKeywordType} Imported & Italian Marble in Kishangarh | Square Feet Marble`;
  const description = `${product.name} is one of the ${seoKeywordType} imported marbles available at Square Feet Marble, Kishangarh. Explore premium marble slabs for luxury projects.`;
  const keywords = `${product.name}, imported marble, Italian marble, Kishangarh marble, Square Feet Marble, marble slabs`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href={`https://www.squarefeetmarble.com/imported-marble/${slug}`}
        />
      </Helmet>

      <div className="container">

        {/* HERO SECTION */}
        <div className="hero-section">

          {/* IMAGE CAROUSEL */}
          <div className="carousel-container">
            <div className="main-image-container">
              <div
                className="carousel-track"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {images.map((img, i) => (
                  <div className="carousel-slide" key={i}>
                    <img
                      src={img}
                      alt={`${product.name} Marble Slab`}
                      loading={i === 0 ? "eager" : "lazy"}
                      decoding="async"
                      width="800"
                      height="600"
                    />
                  </div>
                ))}
              </div>

              {images.length > 1 && (
                <>
                  <button
                    className="carousel-btn prev"
                    onClick={() => changeSlide(-1)}
                  >
                    ❮
                  </button>

                  <button
                    className="carousel-btn next"
                    onClick={() => changeSlide(1)}
                  >
                    ❯
                  </button>
                </>
              )}
            </div>
          </div>

          {/* PRODUCT INFO */}
          <div className="product-info">

            <h1 className="product-title">
              {product.name} Imported Marble
            </h1>

            <p className="product-description">
              {product.description}
            </p>

            {product.inStock && (
              <div className="stock-indicator">
                ✔ In Stock
              </div>
            )}

          </div>

        </div>
        {/* END HERO */}
        {/* SPECIFICATIONS FULL WIDTH */}
        {product.specifications && (
  <div className="specifications-section visible">

    <h2 className="spec-title">
      {product.name} Marble Specifications
    </h2>

    <table className="specs-table">
      <tbody>
        {Object.entries(product.specifications).map(([key, value]) => (
          <tr key={key}>
            <td>{key}</td>
            <td>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>

     </div>
        )}

        {/* RELATED PRODUCTS */}
        {related.length > 0 && (
          <div className="related-section visible">

            <h2 className="section-title">
              Related Products
            </h2>

            <div className="products-grid">
              {related.map((r) => (
                <div className="product-card" key={r.slug}>

                  <div className="product-card-image">
                    <img
                      src={r.images?.[0]}
                      alt={`${r.name} Marble`}
                      loading="lazy"
                      height="300"
                    />
                  </div>

                  <div className="product-card-content">

                    <h3 className="product-card-title">
                      {r.name}
                    </h3>

                    <Link
                      to={`/imported-marble/${r.slug}`}
                      className="view-details-btn"
                    >
                      View Details →
                    </Link>

                  </div>

                </div>
              ))}
            </div>

          </div>
        )}

      </div>
    </>
  );
};

export default ProductPage;